import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { LLM_MODELS } from '@verifyloop/core';
import { IconCheck, IconRefresh } from '@tabler/icons-react';
import { describeError } from '../api/client';
import { clearAdminLlm, getAdminLlm, getAdminLlmModels, setAdminLlm } from '../api/endpoints';
import type { AdminLlmStatus } from '../api/types';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorNotice, LoadingRows, SectionTitle } from '../components/Feedback';
import { CheckboxField, ConfirmButton, SelectField, TextField } from '../components/Form';
import { CUSTOM_MODEL_OPTION, modelSelection, resolveModelSelectValue } from '../lib/modelSelect';
import { Pill } from '../components/Pills';
import { useAuth, useResource, useToast } from '../state';

type ProviderId = 'anthropic' | 'openai' | 'google' | 'custom';

interface ProviderOption {
  value: ProviderId;
  label: string;
  defaultModel: string;
  recommendedModels: readonly string[];
  placeholder: string;
}

const DEFAULT_OPTION: ProviderOption = {
  value: 'anthropic',
  label: 'Anthropic (Claude)',
  defaultModel: 'claude-3-5-sonnet-20241022',
  recommendedModels: ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-latest', 'claude-3-opus-20240229'],
  placeholder: 'claude-3-5-sonnet-20241022 or claude-3-5-haiku-latest',
};

const PROVIDER_OPTIONS: readonly ProviderOption[] = [
  DEFAULT_OPTION,
  {
    value: 'openai',
    label: 'OpenAI (GPT)',
    defaultModel: 'gpt-4o-mini',
    recommendedModels: ['gpt-4o-mini', 'gpt-4o', 'o1-mini'],
    placeholder: 'gpt-4o-mini or gpt-4o',
  },
  {
    value: 'google',
    label: 'Google (Gemini)',
    defaultModel: 'gemini-1.5-flash-latest',
    recommendedModels: ['gemini-1.5-flash-latest', 'gemini-1.5-pro-latest'],
    placeholder: 'gemini-1.5-flash-latest or gemini-1.5-pro-latest',
  },
  {
    value: 'custom',
    label: 'Custom (OpenAI-compatible)',
    defaultModel: 'llama-3.3-70b-versatile',
    recommendedModels: ['llama-3.3-70b-versatile', 'mistral-large-latest'],
    placeholder: 'llama-3.3-70b-versatile or custom model identifier',
  },
];

export function AdminLlmPage(): JSX.Element {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';

  const llmResource = useResource(() => getAdminLlm(), [], {
    enabled: isAdmin,
  });

  const [provider, setProvider] = useState<ProviderId>('anthropic');
  const [model, setModel] = useState<string>('');
  const [customModel, setCustomModel] = useState(false);
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [clearing, setClearing] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [fetchedModels, setFetchedModels] = useState<Array<{ value: string; label: string }>>([]);
  const [fetchingModels, setFetchingModels] = useState<boolean>(false);

  const currentConfig: AdminLlmStatus | undefined = llmResource.data?.provider;

  // Populate form with current values on initial load
  useEffect(() => {
    if (currentConfig?.configured && !initialized) {
      if (currentConfig.provider) {
        const found = PROVIDER_OPTIONS.find((p) => p.value === currentConfig.provider);
        if (found) setProvider(found.value);
        else setProvider('custom');
      }
      if (currentConfig.model) setModel(currentConfig.model);
      if (currentConfig.baseUrl) setBaseUrl(currentConfig.baseUrl);
      if (currentConfig.enabled !== undefined) setEnabled(currentConfig.enabled);
      setInitialized(true);
    }
  }, [currentConfig, initialized]);

  const currentModels = useMemo(() => {
    if (fetchedModels.length > 0) return fetchedModels;
    return (LLM_MODELS[provider] ?? LLM_MODELS.anthropic).map((m) => ({ value: m.value, label: m.label }));
  }, [fetchedModels, provider]);

  const modelOptions = [
    ...currentModels,
    { value: CUSTOM_MODEL_OPTION, label: 'Other / Custom Model Name...' },
  ];
  const selectValue = resolveModelSelectValue(currentModels, model, customModel);

  const handleModelChange = (value: string) => {
    const selection = modelSelection(value);
    setCustomModel(selection.isCustom);
    setModel(selection.model);
  };

  const handleProviderChange = (nextProvider: ProviderId) => {
    setProvider(nextProvider);
    setFetchedModels([]);
    const def = LLM_MODELS[nextProvider]?.[0]?.value ?? '';
    setCustomModel(false);
    setModel(def);
  };

  const handleFetchModels = async () => {
    setFetchingModels(true);
    try {
      const res = await getAdminLlmModels({
        provider,
        baseUrl: baseUrl.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
      });
      if (res.models && res.models.length > 0) {
        const list = res.models.map((m) => ({ value: m.id, label: m.label || m.id }));
        setFetchedModels(list);
        toast.success(`Retrieved ${list.length} model(s) from ${provider === 'custom' ? 'endpoint /models' : provider}!`);
        if (!customModel && (!model || !list.some((m) => m.value === model)) && list[0]) {
          setModel(list[0].value);
        }
      } else {
        toast.info('No models returned from provider /models endpoint');
      }
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setFetchingModels(false);
    }
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!model.trim()) {
      toast.error('Model identifier is required');
      return;
    }
    if (!apiKey.trim() && !currentConfig?.hasKey) {
      toast.error('API key is required for configuring a provider');
      return;
    }

    setSaving(true);
    try {
      await setAdminLlm({
        provider,
        model: model.trim(),
        baseUrl: baseUrl.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        enabled,
      });
      toast.success('Hosted LLM provider saved', `Active provider: ${provider}, model: ${model}`);
      setApiKey('');
      llmResource.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearAdminLlm();
      toast.success('Hosted LLM provider reset', 'Provider has been cleared and is now unconfigured.');
      setApiKey('');
      setModel('');
      setCustomModel(false);
      setBaseUrl('');
      setEnabled(true);
      setInitialized(false);
      llmResource.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setClearing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="page">
        <PageHeader
          title="Super Admin: Hosted LLM"
          subtitle="Configure default LLM provider for test plan generation."
        />
        <div className="notice notice--error" role="alert">
          <div>
            <p className="notice__title">Access Restricted</p>
            <p className="notice__message">
              This area is restricted to operators with the <strong>admin</strong> role. Your account does not have sufficient permissions to view or update hosted LLM credentials.
            </p>
            <p className="muted" style={{ marginTop: '8px', fontSize: '13px' }}>
              Signed in as <strong>{user?.email ?? 'Unknown'}</strong> (role: <code>{user?.role ?? 'member'}</code>)
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Super Admin: Hosted LLM"
        subtitle="Configure the operator-hosted LLM provider used for test plan generation across Justloop."
      />

      {llmResource.error ? (
        <ErrorNotice error={llmResource.error} onRetry={llmResource.reload} />
      ) : null}

      {llmResource.loading ? (
        <LoadingRows rows={4} label="Loading hosted LLM configuration" />
      ) : null}

      {llmResource.data ? (
        <>
          {/* Status Card */}
          <section className="panel">
            <SectionTitle
              actions={
                currentConfig?.configured ? (
                  <ConfirmButton
                    label="Clear Provider"
                    confirmLabel="Confirm Reset?"
                    className="btn btn--danger btn--small"
                    disabled={clearing || saving}
                    title="Remove hosted provider and require callers to use BYOK"
                    onConfirm={() => void handleClear()}
                  />
                ) : null
              }
            >
              Current Provider Status
            </SectionTitle>

            {currentConfig?.configured ? (
              <>
                <dl className="fact-grid">
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <Pill tone={currentConfig.enabled ? 'pass' : 'warn'}>
                        {currentConfig.enabled ? 'Enabled' : 'Disabled'}
                      </Pill>
                    </dd>
                  </div>
                  <div>
                    <dt>Provider</dt>
                    <dd>
                      <strong style={{ textTransform: 'capitalize' }}>
                        {currentConfig.provider ?? '-'}
                      </strong>
                    </dd>
                  </div>
                  <div>
                    <dt>Model</dt>
                    <dd className="mono">{currentConfig.model ?? '-'}</dd>
                  </div>
                  <div>
                    <dt>API Key</dt>
                    <dd>
                      <Pill tone={currentConfig.hasKey ? 'pass' : 'fail'}>
                        {currentConfig.hasKey ? 'Present (Redacted)' : 'Missing Key'}
                      </Pill>
                    </dd>
                  </div>
                  {currentConfig.baseUrl ? (
                    <div>
                      <dt>Base URL</dt>
                      <dd className="mono">{currentConfig.baseUrl}</dd>
                    </div>
                  ) : null}
                </dl>
                <p className="muted" style={{ marginTop: '12px', fontSize: '13px' }}>
                  The hosted provider is active. Callers without their own BYOK key will automatically utilize this model for AI plan generation. Plaintext keys are never transmitted to clients.
                </p>
              </>
            ) : (
              <EmptyState title="No Hosted Provider Configured">
                <p>
                  No hosted LLM provider is currently set. Plan generation requests currently require users to provide their own API key (BYOK).
                </p>
                <p className="muted" style={{ marginTop: '6px' }}>
                  Use the configuration form below to configure a hosted provider for your organization.
                </p>
              </EmptyState>
            )}
          </section>

          {/* Configuration Form */}
          <section className="panel">
            <SectionTitle>Configure Provider</SectionTitle>
            <p className="muted">
              Configure credentials for Anthropic, OpenAI, Google, or a custom OpenAI-compatible endpoint.
            </p>

            <form onSubmit={(e) => void handleSave(e)} style={{ marginTop: '16px' }}>
              <SelectField<ProviderId>
                label="Provider"
                value={provider}
                onChange={handleProviderChange}
                options={PROVIDER_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                hint="Select the AI provider to power test plan generation."
              />

              <TextField
                label="Base URL"
                value={baseUrl}
                onChange={setBaseUrl}
                placeholder={provider === 'custom' ? 'e.g. https://api.groq.com/openai/v1 or https://openrouter.ai/api/v1' : 'Leave empty for provider default'}
                hint={provider === 'custom' ? 'OpenAI-compatible endpoint root (e.g. https://api.groq.com/openai/v1, /v1, or /chat/completions).' : 'Optional override for API proxy or gateway.'}
              />

              <TextField
                label="API Key"
                type="password"
                value={apiKey}
                onChange={setApiKey}
                placeholder={
                  currentConfig?.hasKey
                    ? '•••••••••••••••• (Leave blank to keep existing key)'
                    : 'Enter API key (e.g. sk-...)'
                }
                autoComplete="new-password"
                hint="Your secret key is stored in server-only secure settings and is never exposed in plaintext to the browser."
              />

              <div style={{ margin: '14px 0', padding: '12px 14px', borderRadius: '6px', background: 'var(--color-bg-subtle, rgba(0,0,0,0.03))', border: '1px solid var(--color-border, #e5e7eb)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <strong style={{ fontSize: '13px' }}>Discover Models from /models Endpoint</strong>
                    <p className="muted" style={{ fontSize: '12px', margin: '2px 0 0' }}>
                      Query the live <span className="mono">/models</span> endpoint of this provider using the credentials above.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn--small btn--outline"
                    onClick={() => void handleFetchModels()}
                    disabled={fetchingModels || (provider === 'custom' && !baseUrl.trim())}
                  >
                    <IconRefresh size={14} className={fetchingModels ? 'spinner' : ''} style={{ marginRight: '6px' }} />
                    {fetchingModels ? 'Querying /models...' : 'Fetch Models from /models'}
                  </button>
                </div>
                {fetchedModels.length > 0 ? (
                  <p style={{ marginTop: '8px', fontSize: '12px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <IconCheck size={14} /> Retrieved {fetchedModels.length} models dynamically from endpoint!
                  </p>
                ) : null}
              </div>

              <SelectField
                label="Model"
                value={selectValue}
                onChange={handleModelChange}
                options={modelOptions}
                hint={fetchedModels.length > 0 ? `Select from ${fetchedModels.length} models retrieved from endpoint, or specify custom.` : 'Select the AI model used when users do not provide their own BYOK key.'}
              />

              {selectValue === '__custom__' ? (
                <TextField
                  label="Specify Custom Model Name"
                  value={model}
                  onChange={setModel}
                  placeholder="e.g. meta-llama/Llama-3.3-70B-Instruct"
                  hint="Enter the exact model identifier configured on your endpoint."
                  required
                />
              ) : null}

              <div style={{ marginTop: '12px', marginBottom: '20px' }}>
                <CheckboxField
                  label="Enable this provider for test plan generation"
                  checked={enabled}
                  onChange={setEnabled}
                  hint="When disabled, the configuration is preserved but calls without BYOK will be rejected."
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  type="submit"
                  className="btn"
                  disabled={saving || clearing}
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
                {currentConfig?.configured ? (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => {
                      if (currentConfig.provider) {
                        const found = PROVIDER_OPTIONS.find((p) => p.value === currentConfig.provider);
                        if (found) setProvider(found.value);
                      }
                      setModel(currentConfig.model ?? '');
                      setBaseUrl(currentConfig.baseUrl ?? '');
                      setApiKey('');
                      setEnabled(currentConfig.enabled ?? true);
                    }}
                    disabled={saving || clearing}
                  >
                    Reset Form
                  </button>
                ) : null}
              </div>
            </form>
          </section>
        </>
      ) : null}
    </div>
  );
}
